/**
 * Parajohn product page enhancements for Figma node 797:856.
 * Loaded on all product templates (default product.json and product.parajohn.json).
 * Progressive only — does not replace Horizon cart / variant / form logic.
 */

import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/** sessionStorage key for the Ajax cart snapshot taken before Buy Now checkout */
const BUY_NOW_PRESERVE_KEY = 'rsw-buy-now-preserved-cart';
const BUY_NOW_RESTORE_LOCK = 'rsw-buy-now-restore-lock';

const PDP_ROOT = '.product-information';

/** @type {WeakMap<Element, number>} unit price in minor units (cents) */
const unitPriceByRoot = new WeakMap();

function activeCurrency() {
  return window.Shopify?.currency?.active || 'AED';
}

/**
 * Parse a displayed money string into minor units.
 * Must not use a naive digit strip — currency prefixes like "Dhs." leave a
 * leading "." that parseFloat turns into 0.195 instead of 195.
 * @param {string | null | undefined} text
 * @returns {number | null}
 */
function moneyMinor(text) {
  return convertMoneyToMinorUnits(String(text || ''), activeCurrency());
}

/**
 * Read Shopify cents from a data attribute (authoritative — matches product cards).
 * @param {Element | null | undefined} el
 * @param {string} attr
 * @returns {number | null}
 */
function centsFromDataset(el, attr) {
  if (!(el instanceof HTMLElement)) return null;
  const value = Number.parseInt(el.getAttribute(attr) || '', 10);
  if (Number.isFinite(value) && value > 0) return value;
  return null;
}

/**
 * Unit price in minor units for the current variant.
 * Prefer Liquid/Shopify cents so PDP matches collection cards (avoids "Dhs." parse bugs).
 * @param {Element} root
 * @returns {number | null}
 */
function currentProductPriceMinor(root) {
  const sticky = document.querySelector('sticky-add-to-cart');
  const fromSticky = centsFromDataset(sticky, 'data-variant-unit-price');
  if (fromSticky) return fromSticky;

  const fromRoot = centsFromDataset(root, 'data-variant-unit-price');
  if (fromRoot) return fromRoot;

  const priceBlock = root.querySelector('.product-details product-price');
  const fromPriceBlock = centsFromDataset(priceBlock, 'data-variant-unit-price');
  if (fromPriceBlock) return fromPriceBlock;

  // Fallback: parse DOM only at quantity 1 (unit price, not a rewritten line total)
  if (getProductPageQuantity(root) !== 1) return unitPriceByRoot.get(root) || null;

  const prices = [
    ...root.querySelectorAll(
      '.product-details product-price .price-item__group.price, .product-details product-price > [ref="priceContainer"] > .price'
    ),
  ];
  const saleEl = prices.find((el) => !el.closest('.unit-price, price-per-item')) || prices.at(-1);
  return moneyMinor(saleEl?.textContent);
}

/**
 * @param {Element} root
 * @returns {number | null}
 */
function currentCompareAtMinor(root) {
  const fromRoot = centsFromDataset(root, 'data-variant-compare-at-price');
  if (fromRoot) return fromRoot;

  const priceBlock = root.querySelector('.product-details product-price');
  const fromPriceBlock = centsFromDataset(priceBlock, 'data-variant-compare-at-price');
  if (fromPriceBlock) return fromPriceBlock;

  const compareEl = root.querySelector('.product-details product-price .compare-at-price');
  return moneyMinor(compareEl?.textContent);
}

/**
 * Keep data-* unit prices in sync after variant changes.
 * @param {Element} root
 * @param {{ price?: number, compare_at_price?: number | null } | null | undefined} variant
 */
function applyVariantPriceData(root, variant) {
  if (!variant || typeof variant.price !== 'number') return;

  root.setAttribute('data-variant-unit-price', String(variant.price));
  const compare = typeof variant.compare_at_price === 'number' ? variant.compare_at_price : 0;
  root.setAttribute('data-variant-compare-at-price', String(compare));

  root.querySelectorAll('.product-details product-price').forEach((el) => {
    el.setAttribute('data-variant-unit-price', String(variant.price));
    el.setAttribute('data-variant-compare-at-price', String(compare));
  });

  const sticky = document.querySelector('sticky-add-to-cart');
  if (sticky instanceof HTMLElement) {
    sticky.dataset.variantUnitPrice = String(variant.price);
  }

  unitPriceByRoot.set(root, variant.price);
}

function t(key, fallback = '') {
  const value = globalThis.Theme?.translations?.[key];
  return typeof value === 'string' && value.length ? value : fallback;
}

function deliveryCopyHtml() {
  const timeline = t('delivery_timeline', 'Dubai: 1-2 Days, UAE: 3-5 days');
  const free = t('free_delivery_uae', 'Free Delivery UAE');
  return `${DELIVERY_ICON}<span>${timeline}  <strong>${free}</strong></span>`;
}

function savePercentLabel(percent) {
  return t('save_percent', 'Save [percent]%').replace('[percent]', String(percent));
}

const DELIVERY_ICON = `<svg width="28" height="27" viewBox="0 0 28 27" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M27.6268 23.1418C27.5542 23.2933 27.5009 23.4554 27.407 23.5941C27.119 24.0229 26.686 24.1168 26.2017 24.104C25.6961 24.089 25.694 24.0997 25.5233 24.5626C25.0796 25.7573 23.8806 26.5744 22.639 26.5274C21.3014 26.4762 20.1665 25.608 19.8081 24.3408C19.759 24.1658 19.6993 24.0954 19.5052 24.0954C16.158 24.1018 12.8108 24.0997 9.46357 24.0997C9.39317 24.0997 9.32277 24.1061 9.2353 24.1104C9.04757 24.8954 8.64437 25.5333 7.97877 25.992C7.46037 26.3504 6.8801 26.5317 6.25503 26.521C5.0305 26.504 3.72703 25.7594 3.30463 24.0997C2.91637 24.0997 2.5217 24.0976 2.12917 24.0997C1.38463 24.1061 0.887567 23.5941 0.889701 22.8453C0.893967 18.841 0.893967 14.8368 0.889701 10.8346C0.889701 10.7066 0.840634 10.5701 0.783034 10.4549C-1.40577 6.13063 1.20543 0.933832 5.9905 0.103965C9.90944 -0.576568 13.7153 2.1605 14.2934 6.0709C14.3169 6.2309 14.3404 6.38876 14.3702 6.58076C14.4662 6.58716 14.5708 6.59783 14.6732 6.59783C15.887 6.59997 17.103 6.6213 18.3169 6.59143C19.1553 6.5701 19.6694 7.1269 19.6545 7.91623C19.6204 9.54397 19.6438 11.1738 19.6438 12.8016C19.6438 12.9061 19.6438 13.0128 19.6438 13.1493C19.7697 13.1493 19.8657 13.1493 19.9617 13.1493C21.2481 13.1429 22.5345 13.1578 23.8209 13.1258C24.5782 13.1066 25.0966 13.3989 25.3953 14.0965C25.8433 15.144 26.2998 16.1872 26.7542 17.2325C26.8588 17.4714 26.9782 17.704 27.0742 17.945C27.2641 18.4165 27.4412 18.8922 27.6246 19.3658C27.6268 20.6224 27.6268 21.8832 27.6268 23.1418ZM2.0353 18.1093C7.54357 18.1093 13.0177 18.1093 18.494 18.1093C18.494 14.6426 18.494 11.1952 18.494 7.73276C17.1564 7.73276 15.8444 7.7349 14.5302 7.73063C14.3873 7.73063 14.3532 7.7925 14.3404 7.92477C14.3126 8.2021 14.2828 8.48157 14.2166 8.75037C13.5446 11.4426 11.9084 13.288 9.2481 14.0837C6.6177 14.8709 4.2625 14.2352 2.22943 12.3941C2.17397 12.345 2.11637 12.2981 2.03743 12.232C2.0353 14.2117 2.0353 16.1509 2.0353 18.1093ZM18.5025 19.2698C12.9964 19.2698 7.52437 19.2698 2.04597 19.2698C2.04597 19.8032 2.04597 20.3194 2.04597 20.8656C2.4833 20.8464 2.97183 20.8293 2.9825 21.4053C2.99317 22.0112 2.47477 21.9898 2.0225 22.0026C2.0225 22.2693 2.0289 22.5104 2.02037 22.7514C2.01397 22.9136 2.07797 22.9797 2.23797 22.9754C2.49823 22.969 2.76063 22.9562 3.0209 22.9797C3.24703 22.9989 3.31957 22.9136 3.37717 22.7024C3.99797 20.456 6.77983 19.7264 8.4097 21.3733C8.8513 21.8192 9.1137 22.3589 9.23957 22.9605C12.3393 22.9605 15.4198 22.9605 18.5025 22.9605C18.5025 21.7232 18.5025 20.5029 18.5025 19.2698ZM7.19157 13.2709C10.5558 13.2709 13.2673 10.5722 13.2737 7.2165C13.2801 3.8501 10.5601 1.1109 7.2065 1.10877C3.8081 1.1045 1.10943 3.83303 1.11157 7.2677C1.1137 10.568 3.8529 13.2709 7.19157 13.2709ZM19.6588 18.1093C21.743 18.1093 23.8145 18.1093 25.935 18.1093C25.4017 16.8976 24.8769 15.72 24.3649 14.536C24.2689 14.3141 24.1196 14.2544 23.902 14.2565C22.5793 14.265 21.2588 14.2693 19.9361 14.2757C19.8486 14.2757 19.7612 14.2842 19.6609 14.2885C19.6588 15.5706 19.6588 16.8336 19.6588 18.1093ZM19.6673 22.9648C19.6886 22.969 19.71 22.9733 19.7313 22.9797C19.7548 22.8901 19.7804 22.8026 19.8038 22.713C20.2006 21.192 21.7601 20.2128 23.2918 20.5456C24.5206 20.8122 25.2929 21.5717 25.6321 22.7792C25.6534 22.8517 25.7153 22.952 25.775 22.9669C26.3404 23.1034 26.4876 22.9818 26.4876 22.4122C26.4876 22.0517 26.4876 21.6933 26.4833 21.3328C26.4833 21.2368 26.4684 21.1429 26.462 21.0533C25.822 20.9701 25.6321 20.8314 25.6257 20.4624C25.6214 20.0976 25.7644 19.993 26.4406 19.865C26.4406 19.6666 26.4406 19.4682 26.4406 19.2762C24.1601 19.2762 21.9094 19.2762 19.6673 19.2762C19.6673 20.52 19.6673 21.7424 19.6673 22.9648ZM6.29557 21.6122C5.23743 21.5994 4.3841 22.4336 4.37557 23.4896C4.36703 24.52 5.2097 25.3797 6.24863 25.3989C7.28543 25.4181 8.14943 24.5648 8.16437 23.5088C8.1793 22.472 7.34517 21.625 6.29557 21.6122ZM20.8193 23.4917C20.8172 24.5498 21.6705 25.4096 22.7137 25.401C23.7526 25.3904 24.6017 24.5434 24.606 23.5109C24.6102 22.4592 23.7633 21.6144 22.7052 21.6144C21.6556 21.6122 20.8193 22.4464 20.8193 23.4917Z" fill="black"/><path d="M7.20006 12.1872C4.44379 12.1893 2.2038 9.95785 2.20166 7.20798C2.19953 4.43465 4.43526 2.19465 7.20006 2.19678C9.95206 2.19891 12.1814 4.43465 12.1835 7.19305C12.1857 9.95358 9.95846 12.185 7.20006 12.1872ZM7.20219 3.31038C5.07526 3.30185 3.31953 5.03198 3.31313 7.14611C3.3046 9.32211 5.03473 11.0864 7.18513 11.0949C9.32699 11.1034 11.0977 9.34558 11.0721 7.16531C11.0443 4.90185 9.22673 3.30185 7.20219 3.31038Z" fill="black"/><path d="M7.11905 23.5408C7.11692 24.0165 6.73718 24.3899 6.25932 24.3813C5.79852 24.3749 5.43372 23.9995 5.43372 23.5323C5.43585 23.0544 5.81558 22.6811 6.29345 22.6896C6.76065 22.7003 7.12118 23.0736 7.11905 23.5408Z" fill="black"/><path d="M22.7051 24.3812C22.2358 24.3812 21.8497 23.9908 21.8539 23.5236C21.8603 23.0671 22.2443 22.6917 22.703 22.6917C23.1723 22.6917 23.5648 23.0863 23.5563 23.5492C23.5456 24.0079 23.1638 24.3812 22.7051 24.3812Z" fill="black"/><path d="M7.74627 6.65969C8.07054 6.65969 8.36494 6.65542 8.65934 6.66182C9.054 6.66822 9.2908 6.87729 9.28867 7.20795C9.28654 7.52582 9.054 7.73488 8.67 7.74342C8.2028 7.75195 7.7356 7.75408 7.2684 7.74342C6.84174 7.73275 6.64547 7.52582 6.64334 7.10128C6.63907 6.42715 6.63907 5.75302 6.64334 5.07889C6.64547 4.66929 6.86094 4.41542 7.1916 4.41542C7.52227 4.41329 7.74414 4.67142 7.74627 5.07462C7.75054 5.58875 7.74627 6.10929 7.74627 6.65969Z" fill="black"/></svg>`;

const VIEW_ALL_ICON = `<svg class="rsw-pdp-view-all-icon" xmlns="http://www.w3.org/2000/svg" width="41" height="41" viewBox="0 0 41 41" fill="none" aria-hidden="true" focusable="false"><circle opacity="0.65" cx="20.4444" cy="20.4444" r="19.7143" stroke="currentColor" stroke-width="1.46032"/><path d="M17.5238 27.0159L23.5789 20.9608C23.8641 20.6756 23.8641 20.2133 23.5789 19.9282L17.5238 13.873" stroke="currentColor" stroke-width="2.92063" stroke-linecap="round"/></svg>`;

function isProductPage() {
  return Boolean(document.querySelector(PDP_ROOT));
}

const GALLERY_VIEW_KEY = 'rsw-pdp-gallery-view';

function storedGalleryView() {
  try {
    const value = localStorage.getItem(GALLERY_VIEW_KEY);
    if (value === 'grid' || value === 'carousel') return value;
  } catch {
    /* ignore */
  }
  return '';
}

/**
 * Keep the inactive gallery presentation out of accessibility / hit-testing and
 * demote its images to lazy so the hidden duplicate tree does less work.
 * @param {HTMLElement} gallery
 * @param {'grid' | 'carousel'} view
 */
function syncGalleryViewActivity(gallery, view) {
  const slideshow = gallery.querySelector('slideshow-component');
  const grid = gallery.querySelector('.media-gallery__grid');
  const showGrid = view === 'grid';

  if (slideshow instanceof HTMLElement) {
    slideshow.toggleAttribute('inert', showGrid);
    slideshow.setAttribute('aria-hidden', String(showGrid));
    if (showGrid) {
      slideshow.querySelectorAll('img').forEach((img) => {
        if (!(img instanceof HTMLImageElement)) return;
        img.loading = 'lazy';
        img.removeAttribute('fetchpriority');
      });
    }
  }

  if (grid instanceof HTMLElement) {
    grid.toggleAttribute('inert', !showGrid);
    grid.setAttribute('aria-hidden', String(!showGrid));
    if (!showGrid) {
      grid.querySelectorAll('img').forEach((img) => {
        if (!(img instanceof HTMLImageElement)) return;
        img.loading = 'lazy';
        img.removeAttribute('fetchpriority');
      });
    }
  }
}

function setGalleryView(gallery, view) {
  if (!gallery || (view !== 'grid' && view !== 'carousel')) return;

  const unchanged =
    gallery.dataset.rswGalleryView === view &&
    gallery.dataset.presentation === view &&
    gallery.classList.contains(view === 'grid' ? 'media-gallery--grid' : 'media-gallery--carousel');

  gallery.dataset.rswGalleryView = view;
  gallery.dataset.presentation = view;
  gallery.classList.toggle('media-gallery--grid', view === 'grid');
  gallery.classList.toggle('media-gallery--carousel', view === 'carousel');
  syncGalleryViewActivity(gallery, view);

  gallery.querySelectorAll('[data-rsw-gallery-view]').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    button.setAttribute('aria-pressed', String(button.dataset.rswGalleryView === view));
  });

  try {
    localStorage.setItem(GALLERY_VIEW_KEY, view);
  } catch {
    /* ignore */
  }

  // Skip synthetic resize when the view is already applied (boot path) — avoids
  // cascading layout work across slideshows / header on heavy PDPs.
  if (unchanged) return;

  requestAnimationFrame(() => {
    window.dispatchEvent(new Event('resize'));
  });
}

function applyGalleryView(root) {
  const scope = root || document;
  const preferCarousel = window.matchMedia('(max-width: 749px)').matches;
  scope.querySelectorAll('media-gallery[data-rsw-gallery-toggle]').forEach((gallery) => {
    // Mobile always uses the slideshow; ignore a stored desktop "grid" preference.
    const view = preferCarousel
      ? 'carousel'
      : storedGalleryView() || gallery.dataset.rswGalleryDefault || 'grid';
    setGalleryView(gallery, view);
  });
}

function bindGalleryToggle(root) {
  if (root.dataset.rswGalleryToggleBound === 'true') return;
  root.dataset.rswGalleryToggleBound = 'true';

  root.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-rsw-gallery-view]');
    if (!(button instanceof HTMLButtonElement)) return;
    const gallery = button.closest('media-gallery[data-rsw-gallery-toggle]');
    if (!gallery) return;
    event.preventDefault();
    setGalleryView(gallery, button.dataset.rswGalleryView);
  });
}

/**
 * Materialize deferred grid media from <template> and reveal the next batch of 10.
 * @param {HTMLElement} gallery
 */
function revealNextGalleryBatch(gallery) {
  const grid = gallery.querySelector('.media-gallery__grid');
  const button = gallery.querySelector('[data-rsw-gallery-show-more]');
  const wrap = gallery.querySelector('.media-gallery__show-more-wrap');
  if (!(grid instanceof HTMLElement)) return;

  const deferred = [...grid.querySelectorAll(':scope > li.media-gallery__grid-item--deferred[hidden]')];
  if (!deferred.length) {
    wrap?.remove();
    return;
  }

  const nextBatch = deferred[0].dataset.rswGalleryBatch;

  deferred.forEach((item) => {
    if (!(item instanceof HTMLElement)) return;
    if (item.dataset.rswGalleryBatch !== nextBatch) return;

    const template = item.querySelector(':scope > template[data-rsw-gallery-deferred]');
    if (template instanceof HTMLTemplateElement) {
      item.append(template.content.cloneNode(true));
      template.remove();
    }

    item.hidden = false;
    item.classList.remove('media-gallery__grid-item--deferred');
  });

  const remaining = grid.querySelectorAll(':scope > li.media-gallery__grid-item--deferred[hidden]');
  if (!remaining.length) {
    wrap?.remove();
  } else if (button instanceof HTMLButtonElement) {
    button.setAttribute('aria-expanded', 'true');
  }

  const media = gallery.closest('.product-information__media');
  if (media instanceof HTMLElement) {
    syncMediaAvailableHeight(media);
  }
}

function bindGalleryShowMore(root) {
  if (root.dataset.rswGalleryShowMoreBound === 'true') return;
  root.dataset.rswGalleryShowMoreBound = 'true';

  root.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-rsw-gallery-show-more]');
    if (!(button instanceof HTMLButtonElement)) return;
    const gallery = button.closest('media-gallery');
    if (!gallery || !root.contains(gallery)) return;
    event.preventDefault();
    revealNextGalleryBatch(gallery);
  });
}

function resetZoomedImageFit(dialog) {
  dialog.querySelectorAll('.product-media__drag-zoom-wrapper').forEach((wrap) => {
    wrap.style.setProperty('--drag-zoom-scale', '1');
    wrap.style.setProperty('--drag-zoom-translate-x', '0px');
    wrap.style.setProperty('--drag-zoom-translate-y', '0px');
  });
}

function bindZoomDialogFit(root) {
  root.querySelectorAll('zoom-dialog dialog').forEach((dialog) => {
    if (dialog.dataset.rswZoomFit === 'true') return;
    dialog.dataset.rswZoomFit = 'true';

    const fit = () => resetZoomedImageFit(dialog);
    dialog.addEventListener('toggle', () => {
      if (dialog.open) requestAnimationFrame(fit);
    });
    if (dialog.open) fit();
  });
}

function selectedLabel(fieldset) {
  const checked = fieldset.querySelector('input:checked');
  if (!checked) return '';
  return (checked.dataset.optionTitle || checked.value || '').trim();
}

function optionName(option) {
  const fromInput = option.querySelector('input[data-option-name]')?.dataset.optionName;
  if (fromInput) return fromInput.trim();
  const legend = option.querySelector('legend');
  if (!legend) return '';
  const clone = legend.cloneNode(true);
  clone.querySelectorAll('.variant-option__swatch-value, .rsw-pdp-option-value, .rsw-pdp-size-chart').forEach((el) => el.remove());
  return clone.textContent.replace(/\s+:\s*$/, '').trim();
}

function parseNameHelper(raw) {
  const value = String(raw || '').replace(/\s+/g, ' ').trim();
  const pipe = value.split(' | ');
  if (pipe.length > 1) return { title: pipe[0].trim(), helper: pipe.slice(1).join(' | ').trim() };
  const dash = value.split(' — ');
  if (dash.length > 1) return { title: dash[0].trim(), helper: dash.slice(1).join(' — ').trim() };
  const paren = value.match(/^(.*?)\s*\((.+)\)\s*$/);
  if (paren) return { title: paren[1].trim(), helper: paren[2].trim() };
  return { title: value, helper: '' };
}

function syncVariantLabels(root) {
  root.querySelectorAll('.variant-option').forEach((option) => {
    const legend = option.querySelector('legend');
    const name = optionName(option);
    const isSwatch = option.classList.contains('variant-option--swatches');
    const isSize = /size/i.test(name);
    const isSet = /set/i.test(name);

    option.dataset.rswOption = isSwatch ? 'color' : isSize ? 'size' : isSet ? 'set' : 'option';

    if (legend) {
      const value = selectedLabel(option);
      let valueEl = legend.querySelector('.variant-option__swatch-value, .rsw-pdp-option-value');
      if (isSwatch) {
        if (!valueEl) {
          valueEl = document.createElement('span');
          valueEl.className = 'rsw-pdp-option-value';
          legend.append(valueEl);
        }
        valueEl.textContent = value;
        valueEl.hidden = !value;
      }
    }

    option.querySelectorAll(
      '.variant-option__button-label:not(.variant-option__button-label--has-swatch):not(.variant-option__button-label--size-card)'
    ).forEach((label) => {
      syncOptionHelper(label);
    });
  });
}

function syncOptionHelper(label) {
  const textEl = label.querySelector('.variant-option__button-label__text');
  const input = label.querySelector('input');
  if (!textEl || !input) return;

  const parsed = parseNameHelper(input.dataset.optionTitle || input.value || textEl.dataset.rswOptionRaw || '');
  const title = input.dataset.optionTitle || parsed.title;
  const helperText = (input.dataset.optionDescription || parsed.helper || '').trim();

  if (!textEl.dataset.rswOptionRaw) {
    textEl.dataset.rswOptionRaw = (input.value || '').trim();
  }

  const textNodes = [...textEl.childNodes].filter((node) => node.nodeType === Node.TEXT_NODE);
  if (textNodes[0]) textNodes[0].textContent = title;
  else textEl.insertBefore(document.createTextNode(title), textEl.firstChild);
  textNodes.slice(1).forEach((node) => node.remove());

  let helper = textEl.querySelector('.rsw-pdp-size-helper');
  if (!helperText) {
    helper?.remove();
    return;
  }
  if (!helper) {
    helper = document.createElement('span');
    helper.className = 'rsw-pdp-size-helper';
    textEl.append(helper);
  }
  helper.textContent = helperText;
}

function formatTabbyAmount(total, currency) {
  const amount = (Number(total) / 6).toFixed(2);
  return `${currency} ${amount}`;
}

function getProductPageQuantity(root) {
  const input = root.querySelector('form[data-type="add-to-cart-form"] input[name="quantity"]');
  const value = Number.parseInt(input?.value || '1', 10);
  return Number.isFinite(value) && value > 0 ? value : 1;
}

function syncUnitPriceFromDom(root) {
  const unit = currentProductPriceMinor(root);
  if (unit) unitPriceByRoot.set(root, unit);
}

function updateProductLineTotalPrice(root) {
  let unit = unitPriceByRoot.get(root);
  if (!unit) {
    syncUnitPriceFromDom(root);
    unit = unitPriceByRoot.get(root);
  }
  if (!unit) return;

  const quantity = getProductPageQuantity(root);
  const lineTotal = unit * quantity;
  const moneyFormat = window.Theme?.moneyFormat || '${{amount}}';
  const currency = activeCurrency();

  root.querySelectorAll('.product-details product-price .price-item__group.price, .product-details product-price > [ref="priceContainer"] > .price').forEach((saleEl) => {
    if (saleEl.closest('.unit-price, price-per-item')) return;
    saleEl.textContent = formatMoney(lineTotal, moneyFormat, currency);
  });
}

function updateTabbyAmount(root) {
  const widget = root.querySelector('[data-rsw-tabby]');
  const amountEl = widget?.querySelector('[data-rsw-tabby-amount]');
  if (!widget || !amountEl) return;

  let unit = unitPriceByRoot.get(root);
  if (!unit) {
    syncUnitPriceFromDom(root);
    unit = unitPriceByRoot.get(root);
  }
  if (!unit) return;

  const totalMinor = unit * getProductPageQuantity(root);
  const currency = widget.dataset.currency || activeCurrency();
  const totalMajor = Number.parseFloat(formatMoney(totalMinor, '{{amount}}', currency));
  if (!Number.isFinite(totalMajor)) return;
  amountEl.textContent = formatTabbyAmount(totalMajor, currency);
}

/**
 * Run after other productSelect .then handlers (price/inventory morph) finish.
 * rsw listens in capture phase so its promise callback is often first.
 * @param {() => void} fn
 */
function afterVariantMorph(fn) {
  queueMicrotask(fn);
}

function refreshVariantUi(root, variant, html) {
  if (variant) applyVariantPriceData(root, variant);
  syncVariantLabels(root);
  syncInventoryFromHtml(root, html);
  injectSaveBadge(root);
  syncUnitPriceFromDom(root);
  updateTabbyAmount(root);
  updateProductLineTotalPrice(root);
  bindCustomise(root);
  updateCustomiseSubject(root);
  layoutBuyRow(root);
  injectBuyNow(root);
  applyGalleryView(root);
  bindZoomDialogFit(root);
}

function setVariantPending(root, pending) {
  root.classList.toggle('rsw-pdp-variant-pending', pending);
}

function injectSaveBadge(root) {
  const priceBlock = root.querySelector('.product-details product-price');
  if (!priceBlock) return;

  const priceContainer = priceBlock.querySelector('[ref="priceContainer"]');
  const saleEl = priceBlock.querySelector('.price-item__group.price, span.price');
  const compareEl = priceBlock.querySelector('.compare-at-price');
  // Prefer badge inside the price row; also clear a leftover sibling from older placement
  const existing =
    priceContainer?.querySelector('.rsw-pdp-save') || priceBlock.querySelector(':scope > .rsw-pdp-save');

  if (!saleEl || !compareEl || !priceContainer) {
    existing?.remove();
    return;
  }

  // Use Shopify cents (same source as product cards), not rewritten line-total text
  const sale = currentProductPriceMinor(root) || moneyMinor(saleEl.textContent);
  const compare = currentCompareAtMinor(root) || moneyMinor(compareEl.textContent);
  if (!sale || !compare || compare <= sale) {
    existing?.remove();
    return;
  }

  const percent = Math.round(((compare - sale) / compare) * 100);
  const badge = existing || document.createElement('span');
  badge.className = 'rsw-pdp-save';
  badge.textContent = savePercentLabel(percent);
  // Inside the price row so sale / compare / save stay on one line.
  // product-price.js replaceWith() wipes this node — refreshVariantUi re-injects after morph.
  if (badge.parentElement !== priceContainer || badge.previousElementSibling !== compareEl) {
    compareEl.insertAdjacentElement('afterend', badge);
  }
}

function injectTaxNote(root) {
  const priceBlock = root.querySelector('.product-details product-price');
  if (!priceBlock || root.querySelector('.rsw-pdp-tax')) return;

  const note = document.createElement('p');
  note.className = 'rsw-pdp-tax';
  note.textContent = t('price_inclusive_of_taxes', 'Price inclusive of all taxes');
  priceBlock.insertAdjacentElement('afterend', note);
}

function accordionIcon() {
  const icon = document.createElement('span');
  icon.className = 'rsw-pdp-accordion__icon';
  icon.setAttribute('aria-hidden', 'true');
  return icon;
}

function makeAccordion(title, open) {
  const accordion = document.createElement('details');
  accordion.className = 'rsw-pdp-accordion';
  accordion.open = Boolean(open);

  const summary = document.createElement('summary');
  summary.className = 'rsw-pdp-accordion__summary';
  summary.append(document.createTextNode(title), accordionIcon());
  accordion.append(summary);
  return accordion;
}

function isProductDetailsHeading(text) {
  const value = String(text || '').trim();
  const translated = t('product_details', 'Product Details').trim();
  return /^product\s*details$/i.test(value) || (translated.length > 0 && value === translated);
}

function unwrapProductDetailsAccordions(root) {
  root.querySelectorAll('.rsw-pdp-accordion').forEach((accordion) => {
    const summary = accordion.querySelector(':scope > summary');
    if (!isProductDetailsHeading(summary?.textContent)) return;

    const parent = accordion.parentNode;
    if (!parent) return;

    const body = accordion.querySelector(':scope > .rsw-pdp-accordion__body') || accordion;
    while (body.firstChild) parent.insertBefore(body.firstChild, accordion);
    accordion.remove();
  });
}

function wrapDescriptionAccordion(root) {
  const details = root.querySelector('.product-details');
  if (!details) return;
  if (details.querySelector('.rsw-pdp-accordions')) return;

  unwrapProductDetailsAccordions(details);

  const rte = [...details.querySelectorAll('.text-block.rte')].find(
    (block) =>
      !block.closest('h1') &&
      !block.querySelector('h1') &&
      !block.closest('.rsw-pdp-accordion') &&
      !block.closest('.pdp-title-row')
  );
  if (!rte) return;

  const headings = [...rte.querySelectorAll(':scope > h2, :scope > h3')];
  const parent = rte.parentNode;

  headings.forEach((heading, index) => {
    const title = heading.textContent.trim();
    if (isProductDetailsHeading(title)) {
      heading.remove();
      return;
    }

    const accordion = makeAccordion(title || 'Details', index === 0);
    const body = document.createElement('div');
    body.className = 'rsw-pdp-accordion__body';
    let node = heading.nextSibling;
    const stop = headings[index + 1];
    while (node && node !== stop) {
      const current = node;
      node = node.nextSibling;
      body.append(current);
    }
    heading.remove();
    accordion.append(body);
    parent.insertBefore(accordion, rte);
  });

  if (headings.length > 0 && !rte.textContent.trim()) rte.remove();
}

function placeReviewsUnderTitle(root) {
  const titleRow = root.querySelector('.pdp-title-row');
  if (!titleRow) return;

  let host = titleRow.parentElement?.querySelector(':scope > .rsw-pdp-reviews');
  if (!host) {
    host = document.createElement('div');
    host.className = 'rsw-pdp-reviews';
    titleRow.after(host);
  }

  const previews = [...root.querySelectorAll('.jdgm-preview-badge')].filter((el) => !host.contains(el));
  if (!host.querySelector('.jdgm-preview-badge') && previews[0]) {
    host.append(previews[0]);
  }
}

function layoutBuyRow(root) {
  const buttons = root.querySelector('.product-form-buttons');
  if (!buttons) return;

  const qty = buttons.querySelector('.quantity-selector-wrapper');
  const atcHost =
    buttons.querySelector(':scope > *:has(.add-to-cart-button)') ||
    buttons.querySelector('.rsw-pdp-buy-row > *:has(.add-to-cart-button)') ||
    buttons.querySelector('add-to-cart-component')?.parentElement;
  if (!qty || !atcHost) return;

  let row = buttons.querySelector(':scope > .rsw-pdp-buy-row');
  if (!row) {
    row = document.createElement('div');
    row.className = 'rsw-pdp-buy-row';
    qty.before(row);
  }

  if (qty.parentElement !== row) row.append(qty);
  if (atcHost !== row && atcHost.parentElement !== row) row.append(atcHost);
}

const CART_PLUS_ICON = `<svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M4.2 7.4h17.6v11.2c0 .8-.3 1.5-.9 2-.5.5-1.3.8-2 .8H7.1c-.8 0-1.5-.3-2-.8-.5-.5-.9-1.2-.9-2V7.4Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M9.2 7.4V5.8c0-2 1.6-3.6 3.8-3.6s3.8 1.6 3.8 3.6v1.6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><circle cx="19.4" cy="6.6" r="5.2" fill="#008106"/><path d="M19.4 4.3v4.6M17.1 6.6h4.6" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/></svg>`;

function cartApiRoot() {
  return window.Shopify?.routes?.root || '/';
}

/**
 * Base64url-encode a string for cart permalink `properties` (Shopify docs).
 * @param {string} value
 * @returns {string}
 */
function base64UrlEncode(value) {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

/**
 * Build a Cart API line item from the PDP form (variant, qty, properties, selling plan).
 * @param {HTMLFormElement} form
 * @returns {{ id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number } | null}
 */
function buildBuyNowItem(form) {
  const formData = new FormData(form);
  const id = formData.get('id');
  if (!id) return null;

  const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
  /** @type {Record<string, string>} */
  const properties = {};
  for (const [key, value] of formData.entries()) {
    const match = /^properties\[([^\]]+)\]$/.exec(String(key));
    if (match && String(value).length) properties[match[1]] = String(value);
  }

  /** @type {{ id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number }} */
  const item = { id: Number(id), quantity };
  if (Object.keys(properties).length) item.properties = properties;

  const sellingPlan = formData.get('selling_plan');
  if (sellingPlan) item.selling_plan = Number(sellingPlan);

  return item;
}

/**
 * Snapshot the current Ajax cart so Buy Now can restore it after checkout.
 * Cart permalinks replace the session cart; this keeps the shopper's basket recoverable.
 * @returns {Promise<void>}
 */
async function preserveCartForBuyNow() {
  const rootUrl = cartApiRoot();
  const res = await fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('Unable to read cart');

  const cart = await res.json();
  /** @type {{ id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number }[]} */
  const items = (cart.items || []).map((/** @type {Record<string, any>} */ line) => {
    /** @type {{ id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number }} */
    const saved = {
      id: Number(line.variant_id || line.id),
      quantity: Number(line.quantity) || 1,
    };
    if (line.properties && Object.keys(line.properties).length) {
      /** @type {Record<string, string>} */
      const props = {};
      for (const [key, value] of Object.entries(line.properties)) {
        if (value != null && String(value).length) props[key] = String(value);
      }
      if (Object.keys(props).length) saved.properties = props;
    }
    const sellingPlanId = line.selling_plan_allocation?.selling_plan?.id;
    if (sellingPlanId) saved.selling_plan = Number(sellingPlanId);
    return saved;
  });

  sessionStorage.setItem(BUY_NOW_PRESERVE_KEY, JSON.stringify({ items }));
}

/**
 * Build a Shopify cart permalink that opens checkout with only this variant/qty.
 * @param {{ id: number, quantity: number, properties?: Record<string, string> }} item
 * @returns {string}
 */
function buildBuyNowPermalink(item) {
  const rootUrl = cartApiRoot();
  let url = `${rootUrl}cart/${item.id}:${item.quantity}`;
  if (item.properties && Object.keys(item.properties).length) {
    const encoded = base64UrlEncode(JSON.stringify(item.properties));
    url += `?properties=${encodeURIComponent(encoded)}`;
  }
  return url;
}

/**
 * Notify theme cart UI (bubble / drawer) after a silent cart restore.
 * @param {Record<string, any>} cart
 */
function notifyCartRestored(cart) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [],
      promise: deferred.promise,
    })
  );
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      items: cart.items,
      source: 'rsw-buy-now-restore',
      itemCount: cart.item_count,
      didError: false,
    },
  });
}

/**
 * After Buy Now, restore the shopper's previous Ajax cart (permalinks replace it).
 * @returns {Promise<void>}
 */
async function restorePreservedCartAfterBuyNow() {
  if (sessionStorage.getItem(BUY_NOW_RESTORE_LOCK)) return;
  const raw = sessionStorage.getItem(BUY_NOW_PRESERVE_KEY);
  if (!raw) return;

  sessionStorage.setItem(BUY_NOW_RESTORE_LOCK, '1');
  try {
    /** @type {{ items?: { id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number }[] }} */
    let saved;
    try {
      saved = JSON.parse(raw);
    } catch {
      sessionStorage.removeItem(BUY_NOW_PRESERVE_KEY);
      return;
    }

    const rootUrl = cartApiRoot();
    const clearRes = await fetch(`${rootUrl}cart/clear.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });
    if (!clearRes.ok) throw new Error('Unable to clear Buy Now remnant cart');

    const items = Array.isArray(saved.items) ? saved.items.filter((line) => line?.id && line.quantity > 0) : [];
    if (items.length) {
      const addRes = await fetch(`${rootUrl}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });
      if (!addRes.ok) {
        const errorBody = await addRes.json().catch(() => null);
        throw new Error(errorBody?.description || errorBody?.message || 'Unable to restore cart');
      }
    }

    sessionStorage.removeItem(BUY_NOW_PRESERVE_KEY);

    const cartRes = await fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' } });
    if (cartRes.ok) {
      notifyCartRestored(await cartRes.json());
    }
  } finally {
    sessionStorage.removeItem(BUY_NOW_RESTORE_LOCK);
  }
}

/**
 * Bind one-time restore when returning from Buy Now checkout (including bfcache back).
 */
function bindBuyNowCartRestore() {
  if (window.__rswBuyNowRestoreBound) return;
  window.__rswBuyNowRestoreBound = true;

  const run = () => {
    restorePreservedCartAfterBuyNow().catch((error) => {
      console.error('[rsw-pdp] Failed to restore cart after Buy Now:', error);
    });
  };

  run();
  window.addEventListener('pageshow', run);
}

/**
 * Direct single-product checkout for the selected PDP variant (cart permalink).
 * Does not merge with the existing cart; existing cart is snapshotted and restored on return.
 * Selling plans are unsupported by permalinks — fall back to clear/add/checkout with the same snapshot.
 * @param {Element} root
 * @returns {Promise<boolean>}
 */
async function checkoutCurrentProductOnly(root) {
  const form = root.querySelector('form[data-type="add-to-cart-form"]');
  if (!(form instanceof HTMLFormElement)) return false;
  if (!form.checkValidity()) {
    form.reportValidity();
    return false;
  }

  const item = buildBuyNowItem(form);
  if (!item) return false;

  try {
    await preserveCartForBuyNow();
  } catch (error) {
    console.warn('[rsw-pdp] Could not snapshot cart before Buy Now:', error);
  }

  const rootUrl = cartApiRoot();

  // Permalinks cannot carry selling plans — isolated clear/add only for that case.
  if (item.selling_plan) {
    const clearRes = await fetch(`${rootUrl}cart/clear.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });
    if (!clearRes.ok) throw new Error('Unable to prepare checkout');

    const addRes = await fetch(`${rootUrl}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items: [item] }),
    });
    if (!addRes.ok) {
      const errorBody = await addRes.json().catch(() => null);
      throw new Error(errorBody?.description || errorBody?.message || 'Unable to start checkout');
    }

    window.location.href = `${rootUrl}checkout`;
    return true;
  }

  window.location.href = buildBuyNowPermalink(item);
  return true;
}

function bindBuyNowClick(buyNow, addButton, root) {
  if (buyNow.dataset.rswBound === 'true') return;
  buyNow.dataset.rswBound = 'true';

  buyNow.addEventListener('click', async () => {
    if (addButton.disabled || buyNow.disabled || buyNow.dataset.rswBusy === 'true') return;

    buyNow.dataset.rswBusy = 'true';
    buyNow.disabled = true;
    try {
      const started = await checkoutCurrentProductOnly(root);
      if (!started) {
        buyNow.disabled = Boolean(addButton.disabled);
        buyNow.dataset.rswBusy = 'false';
      }
    } catch (error) {
      console.error('[rsw-pdp] Buy now failed:', error);
      buyNow.disabled = Boolean(addButton.disabled);
      buyNow.dataset.rswBusy = 'false';
    }
  });
}

function setupFloatingBuyNow(root, buyNow, addButton) {
  document.querySelector('.rsw-pdp-buy-now-float')?.remove();

  let bar = document.querySelector('.rsw-pdp-float-bar');
  if (!bar) {
    bar = document.createElement('div');
    bar.className = 'rsw-pdp-float-bar';
    bar.innerHTML = `
      <button type="button" class="rsw-pdp-float-bar__cart" aria-label="${t('add_to_cart', 'Add to cart')}">${CART_PLUS_ICON}</button>
      <button type="button" class="rsw-pdp-float-bar__buy">${t('buy_it_now', 'Buy it Now')}</button>
    `;
    document.body.append(bar);
  } else {
    const existingCart = bar.querySelector('.rsw-pdp-float-bar__cart');
    const existingBuy = bar.querySelector('.rsw-pdp-float-bar__buy');
    existingCart?.setAttribute('aria-label', t('add_to_cart', 'Add to cart'));
    if (existingBuy) existingBuy.textContent = t('buy_it_now', 'Buy it Now');
  }

  const cartBtn = bar.querySelector('.rsw-pdp-float-bar__cart');
  const buyBtn = bar.querySelector('.rsw-pdp-float-bar__buy');
  bar._rswBuyNowTarget = buyNow;
  bar._rswAddToCartTarget = addButton;

  const setFloatingVisible = (visible) => {
    bar.classList.toggle('is-hidden', !visible);
    bar.toggleAttribute('hidden', !visible);
    document.body.classList.toggle('has-rsw-buy-now-float', visible);
  };

  const syncDisabled = () => {
    const unavailable = Boolean(buyNow.disabled || addButton.disabled);
    cartBtn.disabled = Boolean(addButton.disabled);
    buyBtn.disabled = unavailable;
    cartBtn.setAttribute('aria-disabled', String(cartBtn.disabled));
    buyBtn.setAttribute('aria-disabled', String(unavailable));
  };

  if (bar.dataset.rswBound !== 'true') {
    bar.dataset.rswBound = 'true';
    cartBtn.addEventListener('click', () => {
      const target = bar._rswAddToCartTarget;
      if (!target || target.disabled || cartBtn.disabled) return;
      const form = target.closest('form') || document.querySelector('.product-information form[data-type="add-to-cart-form"]');
      if (form && !form.checkValidity()) {
        form.reportValidity();
        return;
      }
      target.click();
    });
    buyBtn.addEventListener('click', () => {
      if (buyBtn.disabled) return;
      bar._rswBuyNowTarget?.click();
    });
  }

  if (bar._rswBuyNowObserver) {
    bar._rswBuyNowObserver.disconnect();
  }
  if (bar._rswBuyNowMutations) {
    bar._rswBuyNowMutations.disconnect();
  }

  syncDisabled();
  setFloatingVisible(true);

  const observeTarget =
    root.querySelector('.rsw-pdp-buy-row') || buyNow || addButton.closest('.product-form-buttons') || buyNow;

  const observer = new IntersectionObserver(
    ([entry]) => {
      const inView = Boolean(entry?.isIntersecting && entry.intersectionRatio > 0);
      setFloatingVisible(!inView);
    },
    {
      threshold: [0, 0.2, 0.5],
      rootMargin: '0px 0px -12% 0px',
    }
  );
  observer.observe(observeTarget);
  bar._rswBuyNowObserver = observer;

  const mutations = new MutationObserver(syncDisabled);
  mutations.observe(buyNow, { attributes: true, attributeFilter: ['disabled'] });
  mutations.observe(addButton, { attributes: true, attributeFilter: ['disabled'] });
  bar._rswBuyNowMutations = mutations;
}

function injectBuyNow(root) {
  layoutBuyRow(root);

  const buttons = root.querySelector('.product-form-buttons');
  const addButton = root.querySelector('.add-to-cart-button:not(.sticky-add-to-cart__button)');
  if (!buttons || !addButton) return;

  let buyNow = buttons.querySelector('.rsw-pdp-buy-now');
  if (!buyNow) {
    buyNow = document.createElement('button');
    buyNow.type = 'button';
    buyNow.className = 'rsw-pdp-buy-now';
    buyNow.textContent = t('buy_it_now', 'Buy it Now');
    buttons.append(buyNow);
  } else {
    buyNow.textContent = t('buy_it_now', 'Buy it Now');
  }

  let buyNowRow = buttons.querySelector(':scope > .rsw-pdp-buy-now-row');
  if (!buyNowRow) {
    buyNowRow = document.createElement('div');
    buyNowRow.className = 'rsw-pdp-buy-now-row';
    buyNow.before(buyNowRow);
  }

  // Remove legacy bag icon next to Buy Now (ATC already sits above in the buy row).
  buyNowRow.querySelectorAll('.rsw-pdp-buy-now-cart').forEach((el) => el.remove());

  if (buyNow.parentElement !== buyNowRow) buyNowRow.append(buyNow);

  const syncPurchaseDisabled = () => {
    if (buyNow.dataset.rswBusy === 'true') return;
    const unavailable = Boolean(addButton.disabled);
    buyNow.disabled = unavailable;
    buyNow.setAttribute('aria-disabled', String(unavailable));
  };
  if (buyNow._rswDisabledObserver) {
    buyNow._rswDisabledObserver.disconnect();
  }
  const purchaseObserver = new MutationObserver(syncPurchaseDisabled);
  purchaseObserver.observe(addButton, { attributes: true, attributeFilter: ['disabled'] });
  buyNow._rswDisabledObserver = purchaseObserver;
  syncPurchaseDisabled();

  bindBuyNowClick(buyNow, addButton, root);
  setupFloatingBuyNow(root, buyNow, addButton);
}

function updateCustomiseSubject(root) {
  const subject = root.querySelector('[data-rsw-customise-subject]');
  if (!subject) return;

  const sizeOption = root.querySelector('.variant-option[data-rsw-option="size"]');
  const size = sizeOption ? selectedLabel(sizeOption) : '';
  const fallback = root.querySelector('[data-rsw-customise]')?.dataset.productTitle || '';
  subject.textContent = size || fallback;
}

/**
 * Keep stock status directly above the Color option (not below Tabby / above Customise).
 * Must sit as a sibling *before* <variant-picker>, never inside it — the picker morphs
 * on every variant change and would destroy nested inventory.
 */
function placeInventoryAboveColor(root) {
  const inventory = root.querySelector('.product-details product-inventory, product-inventory');
  if (!inventory) return;

  const inventoryHost =
    inventory.closest('shopify-block, .shopify-block') || inventory;

  const picker =
    root.querySelector('.product-details variant-picker') || root.querySelector('variant-picker');
  if (!picker) return;

  const pickerHost = picker.closest('shopify-block, .shopify-block') || picker;
  if (!pickerHost.parentElement) return;

  // Pull out if a previous run nested inventory inside the picker form
  if (picker.contains(inventoryHost)) {
    pickerHost.parentElement.insertBefore(inventoryHost, pickerHost);
    return;
  }

  if (inventoryHost.nextElementSibling === pickerHost) return;
  pickerHost.parentElement.insertBefore(inventoryHost, pickerHost);
}

/**
 * If variant-picker morph detached inventory, re-insert it from the section HTML.
 * @param {Element} root
 * @param {Document | Element | null | undefined} html
 */
function syncInventoryFromHtml(root, html) {
  if (!html) {
    placeInventoryAboveColor(root);
    return;
  }

  const fresh = html.querySelector('product-inventory');
  if (!fresh) {
    placeInventoryAboveColor(root);
    return;
  }

  let inventory = root.querySelector('.product-details product-inventory, product-inventory');
  if (!inventory) {
    const freshHost = fresh.closest('shopify-block, .shopify-block') || fresh;
    const picker =
      root.querySelector('.product-details variant-picker') || root.querySelector('variant-picker');
    const pickerHost = picker?.closest('shopify-block, .shopify-block') || picker;
    if (pickerHost?.parentElement) {
      pickerHost.parentElement.insertBefore(document.importNode(freshHost, true), pickerHost);
    }
  }

  placeInventoryAboveColor(root);
}

function setCustomiseOpen(widget, open) {
  const trigger = widget.querySelector('[data-rsw-customise-trigger]');
  const panel = widget.querySelector('[data-rsw-customise-panel]');
  const input = widget.querySelector('[data-rsw-engraving-input]');
  if (!trigger || !panel || !input) return;

  widget.classList.toggle('is-open', open);
  trigger.setAttribute('aria-expanded', String(open));
  panel.setAttribute('aria-hidden', String(!open));
  panel.inert = !open;

  if (open) {
    input.disabled = false;
    input.required = true;
    input.setAttribute('aria-required', 'true');
    window.requestAnimationFrame(() => input.focus());
  } else {
    input.required = false;
    input.removeAttribute('aria-required');
    input.setCustomValidity('');
    input.disabled = true;
  }
}

/**
 * Keep the gallery scrollport inside the visible viewport so every image and
 * the More control can be reached by scrolling the gallery alone.
 * Gallery column only — does not touch product details / page scroll.
 * @param {HTMLElement} media
 */
function syncMediaAvailableHeight(media) {
  if (!window.matchMedia('(min-width: 750px)').matches) {
    media.style.removeProperty('--rsw-pdp-media-available-height');
    return;
  }

  const stickyTop = Number.parseFloat(getComputedStyle(media).top) || 0;
  const rectTop = media.getBoundingClientRect().top;
  const effectiveTop = Math.max(stickyTop, rectTop);
  const available = Math.max(160, Math.round(window.innerHeight - effectiveTop - 16));
  const current = media.style.getPropertyValue('--rsw-pdp-media-available-height');
  const next = `${available}px`;
  if (current !== next) {
    media.style.setProperty('--rsw-pdp-media-available-height', next);
  }
}

/**
 * Route wheel events into the sticky media column scroller so the full
 * gallery can be reached. Only used for Gallery scroll mode
 * (data-pdp-scroll="gallery"). Page scroll mode lets the document scroll
 * the full gallery naturally (Mokobara-style).
 * @param {ParentNode} root
 */
function bindMediaColumnScroll(root) {
  const host = root instanceof Element ? root.closest(PDP_ROOT) || root : null;
  if (host instanceof HTMLElement && host.dataset.pdpScroll === 'page') return;

  const media = root.querySelector('.product-information__media');
  if (!(media instanceof HTMLElement) || media.dataset.rswScrollBound === 'true') return;

  media.dataset.rswScrollBound = 'true';

  const canScrollMedia = () => media.scrollHeight - media.clientHeight > 1;

  const sync = () => syncMediaAvailableHeight(media);

  sync();
  media.addEventListener('pointerenter', sync, { passive: true });
  window.addEventListener('resize', sync, { passive: true });

  // Header sticky size changes the computed `top` offset.
  const header = document.querySelector('#header-component');
  if (header) {
    new MutationObserver(sync).observe(header, {
      attributes: true,
      attributeFilter: ['data-sticky-state', 'data-sticky-size'],
    });
  }

  media.addEventListener(
    'wheel',
    (event) => {
      if (!window.matchMedia('(min-width: 750px)').matches) return;
      sync();
      if (!canScrollMedia()) return;
      if (!(event.target instanceof Node) || !media.contains(event.target)) return;

      const goingUp = event.deltaY < 0;
      const atTop = media.scrollTop <= 0;
      const atBottom = media.scrollTop + media.clientHeight >= media.scrollHeight - 1;

      // At gallery scroll bounds, let the page scroll as before.
      if ((goingUp && atTop) || (!goingUp && atBottom)) return;

      media.scrollTop += event.deltaY;
      event.preventDefault();
    },
    { passive: false }
  );
}

function bindCustomise(root) {
  const widget = root.querySelector('[data-rsw-customise]');
  if (!widget || widget.dataset.rswBound === 'true') return;

  widget.dataset.rswBound = 'true';
  const trigger = widget.querySelector('[data-rsw-customise-trigger]');
  const input = widget.querySelector('[data-rsw-engraving-input]');
  const form = widget.closest('form') || root.querySelector('form[data-type="add-to-cart-form"]');

  trigger?.addEventListener('click', () => {
    setCustomiseOpen(widget, !widget.classList.contains('is-open'));
  });

  input?.addEventListener('keydown', (event) => {
    const isSpace = event.key === ' ' || event.key === 'Spacebar' || event.code === 'Space';
    if (!isSpace) return;
    const inputEl = /** @type {HTMLInputElement} */ (event.target);
    // Block space whenever the caret/selection starts at the beginning.
    if (inputEl.selectionStart === 0) {
      event.preventDefault();
    }
  });

  input?.addEventListener('beforeinput', (event) => {
    const inputEl = /** @type {HTMLInputElement} */ (event.target);
    if (inputEl.selectionStart !== 0) return;
    if (typeof event.data === 'string' && /^\s/.test(event.data)) {
      // Fully-whitespace insert at start (space key / IME) — block it.
      if (/^\s+$/.test(event.data)) {
        event.preventDefault();
        return;
      }
      // Paste like " hello" — strip leading whitespace from the payload.
      event.preventDefault();
      const stripped = event.data.replace(/^\s+/, '');
      if (!stripped) return;
      const { selectionStart, selectionEnd, value } = inputEl;
      const start = selectionStart ?? 0;
      const end = selectionEnd ?? start;
      inputEl.value = `${value.slice(0, start)}${stripped}${value.slice(end)}`;
      const caret = start + stripped.length;
      inputEl.setSelectionRange(caret, caret);
      inputEl.dispatchEvent(new Event('input', { bubbles: true }));
    }
  });

  input?.addEventListener('input', () => {
    const stripped = input.value.replace(/^\s+/, '');
    if (stripped !== input.value) {
      const offset = input.value.length - stripped.length;
      const start = Math.max(0, (input.selectionStart ?? 0) - offset);
      const end = Math.max(0, (input.selectionEnd ?? 0) - offset);
      input.value = stripped;
      input.setSelectionRange(start, end);
    }
    input.setCustomValidity(input.value.trim() ? '' : t('engraving_required', 'Please enter your engraving text.'));
  });

  form?.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest('.add-to-cart-button, .sticky-add-to-cart__button, .rsw-pdp-buy-now, .rsw-pdp-float-bar__cart, .rsw-pdp-float-bar__buy')) return;
      if (!widget.classList.contains('is-open') || !input || input.disabled) return;

      if (!input.value.trim()) {
        input.setCustomValidity(t('engraving_required', 'Please enter your engraving text.'));
        input.reportValidity();
        event.preventDefault();
        event.stopPropagation();
      } else {
        input.setCustomValidity('');
      }
    },
    true
  );

  updateCustomiseSubject(root);
}

function injectDelivery(root) {
  if (root.querySelector('.rsw-pdp-delivery')) return;

  const tax = root.querySelector('.rsw-pdp-tax');
  const priceBlock = root.querySelector('.product-details product-price');
  const anchor = tax || priceBlock;
  if (!anchor) return;

  const line = document.createElement('p');
  line.className = 'rsw-pdp-delivery';
  line.innerHTML = deliveryCopyHtml();
  anchor.insertAdjacentElement('afterend', line);
}

function injectViewAll() {
  const recs = document.querySelector('.product-recommendations .section-resource-list__content');
  if (!recs) return;

  const heading = recs.querySelector('h3, h2, h4');
  if (heading) {
    const normalized = heading.textContent.replace(/\s+/g, ' ').trim();
    if (/^you may also like$/i.test(normalized)) {
      heading.textContent = t('you_may_also_like', 'You May Also Like');
    }
  }

  if (recs.querySelector('.rsw-pdp-view-all')) return;

  const link = document.createElement('a');
  link.className = 'rsw-pdp-view-all';
  link.href = '/collections/all';
  link.innerHTML = `<span class="rsw-pdp-view-all-label">${t('view_all', 'View all')}</span>${VIEW_ALL_ICON}`;
  recs.append(link);
}

function initProductPage(root) {
  const scope = root || document.querySelector(PDP_ROOT);
  if (!scope) return;

  wrapDescriptionAccordion(scope);
  placeReviewsUnderTitle(scope);
  syncVariantLabels(scope);
  placeInventoryAboveColor(scope);
  injectSaveBadge(scope);
  injectTaxNote(scope);
  syncUnitPriceFromDom(scope);
  updateTabbyAmount(scope);
  updateProductLineTotalPrice(scope);
  bindCustomise(scope);
  bindMediaColumnScroll(scope);
  layoutBuyRow(scope);
  injectBuyNow(scope);
  injectDelivery(scope);
  injectViewAll();
  bindGalleryToggle(scope);
  bindGalleryShowMore(scope);
  applyGalleryView(scope);
  bindZoomDialogFit(scope);
  revealSelectedSizeCard(scope);
}

function revealSelectedSizeCard(root) {
  root.querySelectorAll('.variant-option--size-scroll > .variant-option__options').forEach((row) => {
    const current = row.querySelector(
      '.variant-option__button-label--current, .variant-option__button-label[aria-current="page"], .variant-option__button-label:has(:checked)'
    );
    if (!current) return;
    const rowRect = row.getBoundingClientRect();
    const cardRect = current.getBoundingClientRect();
    if (cardRect.left < rowRect.left || cardRect.right > rowRect.right) {
      row.scrollLeft += cardRect.left - rowRect.left;
    }
  });
}

function boot() {
  bindBuyNowCartRestore();
  if (!isProductPage()) return;
  initProductPage();

  const root = document.querySelector(PDP_ROOT);
  if (!root) return;

  let pendingVariantUpdates = 0;

  const handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (!root.contains(event.target)) return;
    if (typeof event.promise?.then !== 'function') return;

    pendingVariantUpdates += 1;
    setVariantPending(root, true);
    syncVariantLabels(root);

    event.promise
      .then((result) => {
        pendingVariantUpdates = Math.max(0, pendingVariantUpdates - 1);
        if (pendingVariantUpdates > 0) return;
        setVariantPending(root, false);
        const variant = result?.detail?.resource;
        const html = result?.detail?.html;
        // Defer so product-price / product-inventory morph run first
        afterVariantMorph(() => refreshVariantUi(root, variant, html));
      })
      .catch((error) => {
        pendingVariantUpdates = Math.max(0, pendingVariantUpdates - 1);
        if (pendingVariantUpdates > 0) return;
        setVariantPending(root, false);
        if (error?.name !== 'AbortError') console.warn('[rsw-pdp] Variant update failed:', error);
        afterVariantMorph(() => refreshVariantUi(root));
      });
  };

  document.addEventListener(StandardEvents.productSelect, handleProductSelect, true);

  root.addEventListener('change', (event) => {
    if (!event.target.closest('.variant-option')) return;
    syncVariantLabels(root);
    injectSaveBadge(root);
    updateTabbyAmount(root);
    updateProductLineTotalPrice(root);
    updateCustomiseSubject(root);
  });

  document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
    if (event.detail?.cartLine) return;
    if (!(event.target instanceof Node) || !root.contains(event.target)) return;
    updateTabbyAmount(root);
    updateProductLineTotalPrice(root);
  });

  document.addEventListener('shopify:section:load', (event) => {
    const sectionRoot = event.target?.querySelector?.(PDP_ROOT) || event.target?.closest?.(PDP_ROOT);
    if (sectionRoot) initProductPage(sectionRoot);
    if (event.target?.querySelector?.('.product-recommendations') || event.target?.matches?.('product-recommendations')) {
      injectViewAll();
    }
  });

  const recs = document.querySelector('product-recommendations');
  if (recs) {
    let viewAllQueued = false;
    const observer = new MutationObserver(() => {
      if (viewAllQueued) return;
      viewAllQueued = true;
      queueMicrotask(() => {
        viewAllQueued = false;
        injectViewAll();
        // Stop once View all is in place — morphs after that would only re-add it.
        if (recs.querySelector('.rsw-pdp-view-all')) observer.disconnect();
      });
    });
    observer.observe(recs, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
